import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, Modal } from 'react-bootstrap';
import { useAiAccess } from '../../AiAccessContext';
import { getDiagnosticTrace, runDiagnosticBatch } from '../../../services/aiDiagnosticsService';
import { getSavedDiagnosticResponses, getSavedMessages } from './chatConversation';
import {
  DIAGNOSTIC_SOURCE,
  clearRunnerResults,
  createRunnerResults,
  getDiagnosticSourceLabel,
  replaceRunnerResults,
  upsertDiagnosticResult,
} from './chatDiagnosticsState';
import {
  formatAllResultsForCopy,
  formatDiagnosticSectionForCopy,
  formatResultForCopy,
  getDiagnosticLabel,
  getDiagnosticSections,
  getField,
  hasInformation,
  splitQuestions,
} from './chatDiagnosticsPresentation';

const codeStyle = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: '360px', overflow: 'auto', fontSize: '12px' };

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const focusedElement = document.activeElement;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  const clipboardContainer = document.querySelector('.modal.show') || document.body;
  clipboardContainer.appendChild(textarea);
  try {
    textarea.select();
    if (!document.execCommand('copy')) throw new Error('Clipboard unavailable.');
  } finally {
    textarea.remove();
    if (focusedElement) focusedElement.focus();
  }
};

const DiagnosticValue = ({ value, name = '', onCopy }) => {
  if (!hasInformation(value)) return null;
  if (Array.isArray(value)) {
    return <div>{value.filter(hasInformation).map((item, index) => <div className='border-left pl-3 mb-3' key={index}><DiagnosticValue value={item} onCopy={onCopy} /></div>)}</div>;
  }
  if (typeof value === 'object') {
    return <dl className='mb-0'>{Object.entries(value).filter(([, item]) => hasInformation(item)).map(([key, item]) => (
      <div className='mb-2' key={key}>
        <dt className='text-muted font-size-sm'>{getDiagnosticLabel(key)}</dt>
        <dd className='mb-0 text-break'><DiagnosticValue value={item} name={key} onCopy={onCopy} /></dd>
      </div>
    ))}</dl>;
  }
  if (typeof value === 'string' && ['answersql', 'scopesql'].includes(name.toLowerCase())) {
    return <>
      <button type='button' className='btn btn-sm btn-light-primary py-1 px-2 mb-2' onClick={() => onCopy(value, 'SQL copied')}>Copy SQL</button>
      <pre className='bg-light rounded p-3 mb-2' style={codeStyle}>{value}</pre>
    </>;
  }
  return <span style={{ whiteSpace: 'pre-wrap' }}>{typeof value === 'boolean' ? value ? 'Yes' : 'No' : String(value)}</span>;
};

const DiagnosticSections = ({ data, onCopy }) => {
  const diagnostics = getField(data, 'diagnostics');
  return <>
    {getDiagnosticSections(data).map(({ title, value }) => <section className='border-top pt-3 mt-3' key={title}>
      <div className='d-flex justify-content-between align-items-start mb-3'>
        <h6 className='font-weight-bold mb-0'>{title}</h6>
        <button type='button' className='btn btn-sm btn-light-primary py-1 px-2' onClick={() => onCopy(formatDiagnosticSectionForCopy(title, value), `${title} copied`)}>Copy Section</button>
      </div>
      <DiagnosticValue value={value} onCopy={onCopy} />
    </section>)}
    {diagnostics && <details className='border-top pt-3 mt-3'><summary className='font-weight-bold'>Raw Diagnostics JSON</summary><pre className='bg-light rounded p-3 mt-3' style={codeStyle}>{JSON.stringify(diagnostics, null, 2)}</pre></details>}
  </>;
};

const DiagnosticResult = ({ result, onCopy, title }) => {
  const diagnostics = getField(result.data, 'diagnostics');
  const answer = getField(getField(result.data, 'response'), 'answer');
  const route = getField(diagnostics, 'route');
  const duration = getField(diagnostics, 'totalDurationMs');
  const correlationId = result.correlationId || getField(diagnostics, 'correlationId');
  const failure = result.error || getField(diagnostics, 'failureReason');

  return <article className='border rounded p-3 mb-3'>
    <div className='d-flex justify-content-between align-items-start mb-2'>
      <h5 className='font-weight-bold text-break mr-3'>{title || result.question}</h5>
      <button type='button' className='btn btn-primary font-weight-bolder font-size-sm text-nowrap' onClick={() => onCopy(formatResultForCopy(result), 'Diagnostic result copied')}>Copy Result</button>
    </div>
    <div className='text-muted font-size-sm mb-2'><strong>Source: </strong>{getDiagnosticSourceLabel(result.source)}</div>
    <div className='text-break mb-2' style={{ whiteSpace: 'pre-wrap' }}><strong>Answer: </strong>{answer || 'No answer returned.'}</div>
    {correlationId && <div className='text-break text-muted font-size-sm mb-2'><strong>Correlation ID: </strong>{correlationId}</div>}
    <div className='text-muted font-size-sm mb-2'>Route: {route || <>&mdash;</>} &middot; Duration: {hasInformation(duration) ? `${duration} ms` : <>&mdash;</>}</div>
    {getField(diagnostics, 'clarificationRequired') === true && <div className='text-warning font-weight-bold mb-2'>Clarification required</div>}
    {failure && <div className='text-danger mb-2' role='status'>{failure}</div>}
    {diagnostics && <details>
      <summary className='font-weight-bold'>Details</summary>
      <DiagnosticSections data={result.data} onCopy={onCopy} />
    </details>}
  </article>;
};

const formatRecentResponseLabel = (response) => {
  const question = response.question.length > 90 ? `${response.question.slice(0, 87)}...` : response.question;
  const timestamp = response.createdAt ? new Date(response.createdAt).toLocaleString() : 'time unavailable';
  return `${timestamp} | ${question} | ${response.correlationId}`;
};

const AiChatDiagnostics = ({ showAlert }) => {
  const auth = useSelector(({ auth: authState }) => authState);
  const { hasAiAccess } = useAiAccess();
  const [open, setOpen] = useState(false);
  const [recentResponses, setRecentResponses] = useState([]);
  const [selectedCorrelationId, setSelectedCorrelationId] = useState('');
  const [correlationIdInput, setCorrelationIdInput] = useState('');
  const [questions, setQuestions] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);
  const responseRequestId = useRef(0);
  const running = useRef(false);
  useEffect(() => () => {
    requestId.current += 1;
    responseRequestId.current += 1;
  }, []);
  const isLocalDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development' && ['localhost', '127.0.0.1', '[::1]', '::1'].includes(window.location.hostname);
  if (!isLocalDevelopment || !auth.isAdmin || !hasAiAccess) return null;

  const copy = async (text, feedback) => {
    try {
      await copyText(text);
      showAlert({ message: feedback, variant: 'success' });
    } catch {
      showAlert({ message: 'Copy failed. Please select and copy the text manually.', variant: 'error' });
    }
  };

  const loadResponse = async (response) => {
    const requestGeneration = responseRequestId.current;
    const key = `existing-${response.correlationId}`;
    setSelectedCorrelationId(response.correlationId);
    setResults((current) => upsertDiagnosticResult(current, {
      ...response,
      key,
      source: DIAGNOSTIC_SOURCE.ExistingChatResponse,
      status: 'running',
    }));
    try {
      const data = await getDiagnosticTrace(response.correlationId);
      if (requestGeneration === responseRequestId.current) {
        setResults((current) => upsertDiagnosticResult(current, {
          ...response,
          key,
          source: DIAGNOSTIC_SOURCE.ExistingChatResponse,
          status: 'complete',
          data,
        }));
      }
    } catch (error) {
      if (requestGeneration === responseRequestId.current) {
        setResults((current) => upsertDiagnosticResult(current, {
          ...response,
          key,
          source: DIAGNOSTIC_SOURCE.ExistingChatResponse,
          status: 'error',
          error: error?.message || 'Diagnostics for this response are unavailable or have expired.',
        }));
      }
    }
  };

  const openDiagnostics = () => {
    const savedMessages = getSavedMessages(sessionStorage, auth.user?.id);
    const responses = getSavedDiagnosticResponses(sessionStorage, auth.user?.id, savedMessages);
    setRecentResponses(responses);
    setSelectedCorrelationId(responses[0]?.correlationId || '');
    setOpen(true);
  };

  const close = () => {
    requestId.current += 1;
    responseRequestId.current += 1;
    setOpen(false);
    setResults([]);
    setQuestions('');
    setCorrelationIdInput('');
    running.current = false;
    setLoading(false);
  };

  const loadSelectedResponse = () => {
    const response = recentResponses.find((item) => item.correlationId === selectedCorrelationId);
    if (response) loadResponse(response);
  };

  const loadCorrelationId = () => {
    const correlationId = correlationIdInput.trim();
    if (!correlationId) return;
    loadResponse({ correlationId, question: '[Question unavailable for manual correlation ID]', createdAt: null });
  };

  const load = async (event) => {
    event.preventDefault();
    const batch = splitQuestions(questions);
    if (running.current || !batch.length) return;
    const currentRequest = ++requestId.current;
    const runnerResults = createRunnerResults(batch, currentRequest);
    running.current = true;
    setLoading(true);
    setResults((current) => replaceRunnerResults(current, runnerResults));
    try {
      await runDiagnosticBatch(batch, (index, result) => {
        setResults((current) => upsertDiagnosticResult(current, {
          ...result,
          key: runnerResults[index].key,
          source: DIAGNOSTIC_SOURCE.Runner,
        }));
      }, () => currentRequest === requestId.current);
    } finally {
      if (currentRequest === requestId.current) {
        running.current = false;
        setLoading(false);
      }
    }
  };

  const clearRunner = () => {
    requestId.current += 1;
    running.current = false;
    setLoading(false);
    setQuestions('');
    setResults((current) => clearRunnerResults(current));
  };

  const copiedResults = results.filter((result) => ['complete', 'error'].includes(result.status));

  return <>
    <div><button type='button' className='btn btn-info font-weight-bolder font-size-sm mr-3' onClick={openDiagnostics}>AI Diagnostic Runner</button></div>
    <Modal centered size='xl' scrollable show={open} onHide={close}>
      <Modal.Header closeButton><Modal.Title>AI Diagnostic Runner</Modal.Title></Modal.Header>
      <Modal.Body>
        <section>
          <h4 className='font-weight-bold mb-2'>AI Diagnostic Runner</h4>
          <p className='text-muted'>Run intentional stateless diagnostic requests. Each line executes the AI pipeline independently with no conversation history.</p>
          <Form onSubmit={load} className='mb-4'>
            <Form.Label htmlFor='ai-diagnostic-questions'>Questions</Form.Label>
            <Form.Control id='ai-diagnostic-questions' as='textarea' rows={20} value={questions} disabled={loading} onChange={(event) => setQuestions(event.target.value)} placeholder='One independent question per line' />
            <Form.Text className='text-muted'>Each non-empty line runs independently, with no conversation history.</Form.Text>
            <div className='d-flex flex-wrap mt-3'>
              <button type='submit' className='btn btn-primary font-weight-bolder font-size-sm mr-3' disabled={loading || !splitQuestions(questions).length}>{loading ? 'Running...' : 'Run All'}</button>
              <button type='button' className='btn btn-danger font-weight-bolder font-size-sm mr-3' onClick={clearRunner}>Clear Runner</button>
            </div>
          </Form>
        </section>

        <section className='border-top pt-4 mt-4'>
          <h5 className='font-weight-bold mb-2'>Load Existing Chat Response</h5>
          <p className='text-muted'>Load an already-recorded trace by correlation ID. This does not rerun the AI pipeline.</p>
          {recentResponses.length > 0 ? <Form.Group>
            <Form.Label htmlFor='ai-response-diagnostic'>Recent chat response</Form.Label>
            <div className='d-flex align-items-start'>
              <Form.Control id='ai-response-diagnostic' as='select' className='mr-3' value={selectedCorrelationId} onChange={(event) => setSelectedCorrelationId(event.target.value)}>
                {recentResponses.map((response) => <option key={response.correlationId} value={response.correlationId}>{formatRecentResponseLabel(response)}</option>)}
              </Form.Control>
              <button type='button' className='btn btn-light-primary text-nowrap' onClick={loadSelectedResponse}>Load Response</button>
            </div>
          </Form.Group> : <div className='text-muted mb-3'>No recent traceable chat responses were found in this browser session.</div>}
          <Form.Group>
            <Form.Label htmlFor='ai-response-correlation-id'>Correlation ID</Form.Label>
            <div className='d-flex align-items-start'>
              <Form.Control id='ai-response-correlation-id' className='mr-3' value={correlationIdInput} onChange={(event) => setCorrelationIdInput(event.target.value)} placeholder='Enter a correlation ID' />
              <button type='button' className='btn btn-light-primary text-nowrap' disabled={!correlationIdInput.trim()} onClick={loadCorrelationId}>Load by ID</button>
            </div>
          </Form.Group>
        </section>

        <section className='border-top pt-4 mt-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h4 className='font-weight-bold mb-0'>Results</h4>
            <button type='button' className='btn btn-info font-weight-bolder font-size-sm' disabled={!copiedResults.length} onClick={() => copy(formatAllResultsForCopy(copiedResults), 'All diagnostics copied')}>Copy All</button>
          </div>
          {!results.length && <div className='text-muted'>Run a diagnostic question or load an existing chat response to see results.</div>}
          {results.map((result, index) => {
            const completed = ['complete', 'error'].includes(result.status);
            return !completed
              ? <article className='border rounded p-3 mb-3' key={result.key}><strong>{index + 1}. {result.question}</strong><div className='text-muted font-size-sm'>Source: {getDiagnosticSourceLabel(result.source)}</div><div className='text-muted' role='status'>{result.status === 'running' ? 'Running...' : 'Pending'}</div></article>
              : <DiagnosticResult key={result.key} result={result} onCopy={copy} title={`${index + 1}. ${result.question}`} />;
          })}
        </section>
      </Modal.Body>
      <Modal.Footer><button type='button' className='btn btn-sm btn-secondary' onClick={close}>Close</button></Modal.Footer>
    </Modal>
  </>;
};

export { DiagnosticResult, formatRecentResponseLabel };
export default AiChatDiagnostics;
